import { defineComponent } from 'vue';
import { QBtn, QBanner, QSpinner } from 'quasar';
import utils from '../../../../../Shared/utils.jsx';
import SchemaForm from '../../../schemaform/SchemaForm.jsx';
import ProjectEventMap from './ProjectEventMap.jsx';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';
import './projectEventMap.css';

export default defineComponent({
    name: 'flow-v2-project-settings-panel',
    components: { QBtn, QBanner, QSpinner, SchemaForm, ProjectEventMap },
    props: {
        projectId: { type: String, required: true },
        root: { type: Object, default: null },
        channel: { type: Object, default: null },
        scopeitems: { type: Object, default: null },
        controlscope: { type: Object, default: null },
    },
    emits: ['saved'],
    data() {
        return {
            loading: true,
            saving: false,
            error: null,
            schema: null,
            model: null,
        };
    },
    watch: {
        projectId: { immediate: true, handler() { this.load(); } },
    },
    methods: {
        async load() {
            if (!this.projectId) return;
            this.loading = true;
            this.error = null;
            try {
                const schema = await utils.schema.get('schema/public/Platform.Schema.Flow.v2/Project');
                const settings = await utils.api.get(`Apps/FlowV2/GetProject?ID=${this.projectId}`);
                if (!Array.isArray(settings.Events))
                    settings.Events = [];
                // Store the plain project object on data (Vue makes it reactive).
                // Do not wrap with ref() — refs assigned to data are auto-unwrapped,
                // so cmodel={this.model.value} would pass undefined and blank Channel.
                this.schema = schema;
                this.model = settings;
            }
            catch (err) {
                this.error = err?.message || String(err);
            }
            finally {
                this.loading = false;
            }
        },
        async save() {
            if (!this.model) return;
            this.saving = true;
            this.error = null;
            try {
                if (Array.isArray(this.model.Events))
                    this.model.Events = this.model.Events.filter((row) => row?.EventName && row?.FlowID);
                const res = await utils.api.request('POST', 'Apps/FlowV2/SaveProject', this.model);
                if (!res.Success)
                    this.error = res.Reason || 'Save failed';
                else
                    this.$emit('saved', this.model);
            }
            catch (err) {
                this.error = err?.message || String(err);
            }
            finally {
                this.saving = false;
            }
        },
    },
    render() {
        if (this.loading) {
            return (
                <div class="q-pa-lg flex flex-center">
                    <QSpinner color="primary" size="40px" />
                </div>
            );
        }
        if (this.error && !this.model) {
            return <QBanner class="bg-red-1 text-negative">{this.error}</QBanner>;
        }
        return (
            <div class="flow-v2-project-settings">
                {this.error ? <QBanner class="bg-red-1 text-negative">{this.error}</QBanner> : null}
                {this.schema && this.model ? (
                    <div class="flow-v2-project-settings__channel">
                        <SchemaForm
                            name="projSettingsForm"
                            root={this.root}
                            schema={this.schema}
                            form={['ChannelType']}
                            cmodel={this.model}
                            parentType="VerticalStack"
                            scopeitems={this.scopeitems}
                            controlscope={this.controlscope}
                            child={0}
                        />
                    </div>
                ) : null}
                {this.schema && this.model ? (
                    <ProjectEventMap
                        class="flow-v2-project-settings__board"
                        model={this.model}
                        schema={this.schema}
                        projectId={this.projectId}
                        channel={this.channel}
                    />
                ) : null}
                <div class="flow-v2-project-settings__save row justify-end">
                    <QBtn
                        color="primary"
                        unelevated
                        no-caps
                        class={`${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`}
                        label="Save"
                        loading={this.saving}
                        onClick={() => this.save()}
                    />
                </div>
            </div>
        );
    },
});
